import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import {
  type Client,
  type Cohort,
  type Contact,
  type PicklistItem,
} from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import {
  EditRecord,
  initialState,
} from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base';
import {
  setAsyncBelongsTo,
  setAsyncHasMany,
} from '@efdigital/ll-admin-v2/data/async-setter';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import { generateCohort } from '@efdigital/ll-admin-v2/data/generators/cohort-generator';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import ImagePickerBig from '../pickers/image-picker-big.gts';
import { PicklistItemPicker } from '../pickers/picklist-item-picker.gts';
import { Validation, type ValidationFailure } from '../validation.gts';

export class EditCohort extends Component<EditRecordSignature<Cohort>> {
  @service declare store: Store;
  @service declare picklists: PicklistsService;

  get startsDate() {
    if (!this.args.item.starts) {
      return '';
    }
    return this.args.item.starts.split('T')[0];
  }

  get endsDate() {
    if (!this.args.item.ends) {
      return '';
    }
    return this.args.item.ends.split('T')[0];
  }

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  handleClientSelect = (client: Client | null) => {
    setAsyncBelongsTo(this.args.item, 'client', client);
  };

  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    if (!contacts.find((item) => item.id === contact.id)) {
      setAsyncHasMany(this.args.item, 'contacts', [...contacts, contact]);
    }
  };

  setAbstract = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.args.item.abstract = target.value;
  };

  setIcon = (icon: string) => {
    this.args.item.icon = icon;
  };

  setStarts = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.starts = target.value;
  };

  setEnds = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.ends = target.value;
  };

  validate = (item: Cohort) => {
    const failures: ValidationFailure[] = [];
    if (!item.icon) {
      failures.push({
        message: 'Icon is required',
        attribute: 'icon',
      });
    }
    if (!item.starts) {
      failures.push({
        message: 'Start date is required',
        attribute: 'starts',
      });
    }
    if (!item.ends) {
      failures.push({
        message: 'End date is required',
        attribute: 'ends',
      });
    }
    if (item.starts > item.ends) {
      failures.push({
        message: 'Start date must be before end date',
        attribute: 'starts',
      });
    }
    return Validation.fromFailures(failures);
  };
  save = async () => {
    const cohort = this.args.item;
    const isNew = cohort.isNew;

    await deepSave(cohort, this.store, { maxDepth: 2 });

    if (isNew) {
      await generateCohort(cohort, this);
      await deepSave(cohort, this.store, { maxDepth: 2 });
    }

    return cohort;
  };

  setDeliveryFinancialYear = (deliveryFinancialYear: PicklistItem | null) => {
    this.args.item.deliveryFinancialYear = deliveryFinancialYear?.key ?? '';
  };

  setDeliveryType = (deliveryType: PicklistItem | null) => {
    this.args.item.deliveryType = deliveryType?.key ?? '';
  };

  setRegion = (region: PicklistItem | null) => {
    this.args.item.region = region?.key ?? '';
  };

  <template>
    <EditRecord
      @editLabel="Edit Cohort"
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @initialState={{initialState @item}}
      @validate={{this.validate}}
      @save={{this.save}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Listing</legend>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-title"
              class="form-label required"
            >Title</label>
            <div>
              <input
                name="title"
                type="text"
                class="form-control"
                id="{{uniqueId}}-title"
                value={{@item.title}}
                required
                {{debounce this.setTitle}}
              />
            </div>
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-abstract"
              class="form-label required"
            >Abstract</label>
            <div>
              <textarea
                name="abstract"
                class="form-control"
                id="{{uniqueId}}-abstract"
                value={{@item.abstract}}
                required
                {{debounce this.setAbstract}}
              ></textarea>
            </div>
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-icon"
              class="form-label required"
            >Icon</label>
            <div>
              <ImagePickerBig
                @value={{@item.icon}}
                @onChange={{this.setIcon}}
                @sizeHint="cohort-icon"
                @defaultTitle={{@item.title}}
                @readonly={{isReadOnly}}
                @required={{true}}
                @id="{{uniqueId}}-icon"
                @placeholder="https://"
              />
            </div>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Reporting</legend>

          <div class="row mb-3">
            <PicklistItemPicker
              @group="delivery-financial-year"
              @label="Delivery Financial Year"
              @name="reportingTemplate"
              @selected={{@item.deliveryFinancialYear}}
              @readonly={{isReadOnly}}
              @onSelect={{this.setDeliveryFinancialYear}}
              class="col-md-4 required"
            />
            <PicklistItemPicker
              @group="region"
              @label="Region"
              @name="region"
              @selected={{@item.region}}
              @readonly={{isReadOnly}}
              @onSelect={{this.setRegion}}
              class="col-md-4 required"
            />

            <PicklistItemPicker
              @group="delivery-type"
              @label="Delivery Type"
              @name="deliveryType"
              @selected={{@item.deliveryType}}
              @readonly={{isReadOnly}}
              @onSelect={{this.setDeliveryType}}
              class="col-md-4 required"
            />

          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Dates</legend>

          <div class="row">
            <div class="col-md">
              <div class="mb-3">
                <label
                  for="{{uniqueId}}-starts"
                  class="form-label required"
                >Cohort start date</label>
                <div>
                  <input
                    name="starts"
                    type="date"
                    class="form-control"
                    id="{{uniqueId}}-starts"
                    value={{this.startsDate}}
                    required
                    {{on "change" this.setStarts}}
                  />
                </div>
              </div>
            </div>
            <div class="col-md">
              <div class="mb-3">
                <label
                  for="{{uniqueId}}-ends"
                  class="form-label required"
                >Cohort end date</label>
                <div>
                  <input
                    name="ends"
                    type="date"
                    class="form-control"
                    id="{{uniqueId}}-ends"
                    value={{this.endsDate}}
                    required
                    {{on "change" this.setEnds}}
                  />
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
